<template>
  <div
    :id="message.id"
    :class="[
      'chat-message-header',
      {
        'view-only': viewOnly,
        'error-state': message.error,
        'is-by-sender': isBySender,
      },
      {
        removed: isRemovedParticipant,
        draft: message.isDraft,
      },
    ]"
    @click="message.error && $emit('retry-send', message)"
  >
    <div v-if="showMessageActions" class="message-actions-container">
      <FriedIconButton
        v-if="showDeleteButton"
        v-tooltip="{
          content: t('dr_delete_message'),
          placement: Placement.Top,
          distance: 8,
          delay: { show: 300, hide: 100 },
        }"
        :data-external="`name:${name}-message:${message.text}-delete-text-button`"
        class="delete-button"
        active
        @click="$emit('delete', message.id)"
      >
        <FriedIcon icon="delete-bin" :size="IconSize.Small" />
      </FriedIconButton>
      <FriedIconButton
        v-else-if="showReplyButton"
        v-tooltip="{
          content: replyMessageTooltip,
          placement: Placement.Top,
          distance: 8,
          delay: { show: 300, hide: 100 },
        }"
        :data-external="`name:${name}-message:${message.text}-reply-to-button`"
        class="reply-button"
        active
        @click="$emit('reply', message)"
      >
        <FriedIcon icon="email-action-reply" :size="IconSize.Small" />
      </FriedIconButton>
    </div>
    <Avatar
      v-if="isLastConsecutiveMessage"
      :class="[{ error: message.error }]"
      :name="isRemovedParticipant ? null : senderFullName"
      :size="AvatarSize.Small"
      :thumb-url="senderThumbUrl"
      :deleted-at="senderDeletedAt"
    />
    <div :class="['message-content', { error: message.error }]">
      <FriedParagraph
        class="message-text"
        :data-external="`name:${name}-message-content:${message.text}`"
      >
        {{ message.text }}
      </FriedParagraph>
    </div>
    <MessageReadBy
      v-if="lastReadsExcludingSender.length"
      class="read-by-container"
      :reads="lastReadsExcludingSender"
      :current-chat-user="currentChatUser"
    />
    <div v-if="message.error" class="error-container">
      <FriedIcon icon="alert-circle" :size="IconSize.Small" />
      <FriedParagraph>{{ t('dr_chat_could_not_send_click_to_try_again') }}</FriedParagraph>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType, ComputedRef } from 'vue';
import { computed, defineComponent } from 'vue';
import { ParagraphSize, AvatarSize, IconSize, Placement } from '@getaccept/fried-tofu';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import type { ChatParticipant, ChatParticipantId, LastReadBy, Message } from '../types';
import ChatHelper from '../helpers/chat.helper';
import type { Participant } from '../../types';
import Avatar from '../../components/Avatar.vue';
import AvatarHelper from '../../helpers/avatar.helper';
import MessageReadBy from './MessageReadBy.vue';

export default defineComponent({
  components: {
    Avatar,
    MessageReadBy,
  },
  props: {
    participants: {
      type: Array as PropType<Array<Participant>>,
      required: true,
    },
    message: {
      type: Object as PropType<Message>,
      required: true,
    },
    messages: {
      type: Array as PropType<Array<Message>>,
      required: true,
    },
    currentChatParticipant: {
      type: Object as PropType<ChatParticipant>,
      default: undefined as ChatParticipant,
    },
    locale: {
      type: String,
      default: 'en-US',
    },
    viewOnly: {
      type: Boolean,
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
    lastReads: {
      type: Array as PropType<Array<LastReadBy>>,
      default: () => [],
    },
    currentChatUser: {
      type: Object as PropType<ChatParticipantId>,
      default: () =>
        ({
          participantId: '',
          userId: '',
        }) as ChatParticipantId,
    },
  },
  emits: ['delete', 'reply', 'retry-send'],
  setup(props) {
    const { t } = useI18n();

    const { name, date, fullTimestamp, isBySender, replyMessage } = ChatHelper.getMessageState(
      props.message,
      props.participants,
      props.messages,
      props.currentChatParticipant,
      props.locale
    );

    const sender: ComputedRef<Partial<Participant>> = computed(() => {
      const owner = props.message?.owner;
      const ownerId = props.message?.ownerId;

      if (!owner && !ownerId) {
        return {
          id: undefined,
          fullName: t('dr_chat_previous_owner'),
        };
      }

      const ownerParticipant = props.participants.find(({ id }) => id === ownerId);

      if (ownerParticipant) {
        return ownerParticipant;
      }

      if (owner?.participantId) {
        const participant = props.participants.find(({ id }) => id === owner.participantId);
        return (
          participant || {
            id: undefined,
            fullName: t('dr_chat_removed_participant'),
          }
        );
      }

      if (owner?.userId) {
        const participant = props.participants.find(({ id }) => id === owner.userId);

        return (
          participant || {
            id: undefined,
            fullName: t('dr_chat_previous_owner'),
          }
        );
      }

      return {
        id: undefined,
        fullName: t('dr_chat_previous_owner'),
      };
    });

    const isRemovedParticipant = computed(
      () => sender?.value.id === undefined || !!sender.value?.deletedAt
    );

    const isLastConsecutiveMessage = computed(() => {
      const index = props.messages.findIndex(msg => msg === props.message);
      if (index === -1) return false;

      const nextMessage = props.messages[index + 1];
      return !nextMessage || nextMessage.ownerId !== props.message.ownerId;
    });

    const senderThumbUrl = computed(() => sender.value?.thumbUrl ?? '');
    const senderDeletedAt = computed(() => sender.value?.deletedAt);

    const formattedTimestamp = computed(() =>
      DateTime.fromMillis(props.message.createdAt)
        .setLocale(props.locale)
        .toLocaleString(DateTime.TIME_SIMPLE)
    );

    const senderFullName = computed(() =>
      sender.value ? AvatarHelper.getDisplayName(sender.value) : ''
    );

    const replyMessageTooltip = computed(() => `${t('dr_reply_to')} ${senderFullName.value}`);

    const showMessageActions = computed(
      () => !props.viewOnly && props.canEdit && !props.message.isDraft
    );

    const showDeleteButton = computed(() => isBySender && !props.message.error);

    const showReplyButton = computed(() => !isBySender && !isRemovedParticipant.value);

    const messageLastReads = computed(() =>
      props.lastReads.filter(({ messageId }) => messageId === props.message.id)
    );

    const lastReadsExcludingSender = computed(() =>
      messageLastReads.value.filter(
        ({ readBy }) =>
          !ChatHelper.isSameParticipant(props.message.owner, readBy.participant) &&
          props.message.ownerId !== readBy.participantId
      )
    );

    return {
      name,
      senderThumbUrl,
      date,
      fullTimestamp,
      isBySender,
      replyMessage,
      IconSize,
      sender,
      formattedTimestamp,
      AvatarSize,
      ParagraphSize,
      t,
      replyMessageTooltip,
      senderFullName,
      isRemovedParticipant,
      Placement,
      showMessageActions,
      showDeleteButton,
      showReplyButton,
      senderDeletedAt,
      isLastConsecutiveMessage,
      lastReadsExcludingSender,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '../../scss/removed-participant';
@import '../../scss/blink';

.chat-message-header {
  display: grid;
  grid-template-columns: 2rem auto 2rem;
  grid-template-rows: auto auto;
  column-gap: var(--spacing-75);
  padding: calc(var(--spacing-25) / 2) var(--spacing-150);
  align-self: flex-start;

  @media screen and (max-width: $sm) {
    padding: calc(var(--spacing-25) / 2) var(--spacing-100);
  }

  .message-actions-container {
    grid-column-start: 3;
    grid-row: 1;
    align-self: center;

    .delete-button,
    .reply-button {
      visibility: hidden;
      height: 2rem;
      width: 2rem;
      margin: auto 0;
      cursor: pointer;

      @media screen and (max-width: $xs) {
        visibility: visible;
      }
    }
  }

  &:hover .message-actions-container .delete-button,
  &:hover .message-actions-container .reply-button {
    visibility: visible;
  }

  .message-content {
    position: relative;
    display: flex;
    flex-direction: column;
    grid-column-start: 2;
    max-width: 584px;
    border-radius: var(--radius-medium);
    border-bottom-left-radius: 0;
    padding: var(--spacing-50) var(--spacing-100);
    background: var(--gray-96);

    .message-text {
      overflow-wrap: anywhere;
      white-space: pre-line;
    }
  }

  .read-by-container {
    display: flex;
    flex-direction: row;
    grid-column-start: 2;
    grid-row: 2;
    justify-content: flex-end;
  }

  .avatar {
    grid-column-start: 1;
    grid-row: 1;
    align-self: flex-end;
  }

  &.is-by-sender {
    align-self: flex-end;

    .message-actions-container {
      grid-column-start: 1;
      grid-row: 1;
    }

    .message-content {
      grid-column-start: 2;
      grid-row: 1;
      background: var(--blue-45);
      border-bottom-right-radius: 0;
      border-bottom-left-radius: var(--radius-medium);

      .message-text {
        color: var(--white);
      }
    }

    .avatar {
      grid-column-start: 3;
      grid-row: 1;
      align-self: flex-end;
    }
  }

  &.no-bottom-padding {
    padding-bottom: 0;
  }

  &.no-padding {
    padding: 0;
  }

  &.view-only {
    padding: var(--spacing-100) var(--spacing-50);
    border: 1px solid var(--gray-94);
    border-radius: var(--radius-medium);

    &:hover {
      background: none;
    }
  }

  &.error-state:hover {
    cursor: pointer;
  }

  .error-container {
    display: flex;
    gap: var(--spacing-50);
    align-items: center;
    color: var(--error-dark);

    p {
      color: var(--error-dark);
    }
  }

  .error,
  .draft {
    opacity: 0.5;
  }
}
</style>
